import type { ContactSectionPayload, ContactSectionResponse, ContactSectionsListResponse } from '@/types';

import { atom } from 'jotai';
import { atomWithQuery } from 'jotai-tanstack-query';

import { apiClient } from '@/utils/api';
import { buildApiParams } from '@/utils/helpers';
import { createPaginationParams, createTableQueryOptions } from '@/components/table';

const CONTACT_SECTIONS_BASE = '/contact-sections';

export type ContactSectionsListParams = {
  page: number;
  per_page: number;
  filters: Record<string, string | undefined>;
  sort?: string;
  include?: string;
  refresh?: number;
};

export const contactSectionsListParamsAtom = atom<ContactSectionsListParams>({
  ...createPaginationParams(),
  filters: {},
  include: undefined
});

export const contactSectionsHydratedAtom = atom<boolean>(false);

export const contactSectionsQueryAtom = atomWithQuery<ContactSectionsListResponse>((get) => {
  const params = get(contactSectionsListParamsAtom);
  const hydrated = get(contactSectionsHydratedAtom);

  return createTableQueryOptions({
    queryKey: ['contact-sections', params] as const,
    queryFn: async () => {
      const apiParams = buildApiParams(
        {
          page: params.page,
          per_page: params.per_page,
          sort: params.sort,
          include: params.include
        },
        params.filters || {}
      );

      const { data } = await apiClient.get(CONTACT_SECTIONS_BASE, { params: apiParams });
      return data as ContactSectionsListResponse;
    },
    enabled: hydrated
  });
});

export const contactSectionDetailIdAtom = atom<number | null>(null);
export const contactSectionDetailRefreshAtom = atom<number>(0);

export const contactSectionDetailQueryAtom = atomWithQuery<ContactSectionResponse>((get) => {
  const sectionId = get(contactSectionDetailIdAtom);
  const refresh = get(contactSectionDetailRefreshAtom);

  return createTableQueryOptions({
    queryKey: ['contact-section-detail', sectionId, refresh] as const,
    queryFn: async () => {
      if (!sectionId) {
        throw new Error('Contact section id is required');
      }

      const { data } = await apiClient.get(`${CONTACT_SECTIONS_BASE}/${sectionId}`);
      return data as ContactSectionResponse;
    },
    enabled: Boolean(sectionId)
  });
});

const touchContactSectionsList = (get: any, set: any) => {
  const currentParams = get(contactSectionsListParamsAtom);
  set(contactSectionsListParamsAtom, { ...currentParams, refresh: Date.now() });
};

const touchContactSectionDetail = (get: any, set: any, sectionId?: number) => {
  const activeId = get(contactSectionDetailIdAtom);
  if (sectionId && activeId && sectionId !== activeId) {
    return;
  }
  set(contactSectionDetailRefreshAtom, Date.now());
};

export const createContactSection = atom(
  null,
  async (get, set, payload: ContactSectionPayload) => {
    await apiClient.post(CONTACT_SECTIONS_BASE, payload);

    touchContactSectionsList(get, set);
  }
);

export const updateContactSection = atom(
  null,
  async (get, set, { id, payload }: { id: number; payload: ContactSectionPayload }) => {
    await apiClient.put(`${CONTACT_SECTIONS_BASE}/${id}`, payload);

    touchContactSectionsList(get, set);
    touchContactSectionDetail(get, set, id);
  }
);

export const deleteContactSection = atom(
  null,
  async (get, set, id: number) => {
    await apiClient.delete(`${CONTACT_SECTIONS_BASE}/${id}`);

    touchContactSectionsList(get, set);
    touchContactSectionDetail(get, set, id);
  }
);

export const contactSectionsAllAtom = atomWithQuery<ContactSectionsListResponse>(() =>
  createTableQueryOptions({
    queryKey: ['contact-sections', 'all'] as const,
    queryFn: async () => {
      const { data } = await apiClient.get(CONTACT_SECTIONS_BASE, {
        params: { per_page: 100, include: undefined }
      });
      return data as ContactSectionsListResponse;
    }
  })
);

